import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import {
  TextField,
  Stack,
  Grid,
  Select,
  MenuItem,
  SelectChangeEvent,
  Autocomplete,
  Alert,
  AlertColor
} from "@mui/material";
import RadioGenerator from "components/shared/RadioGenerator/RadioGenerator";
import Button from "components/shared/Button/Button";
import API from "api";
import { StyledContainer } from "../../PatientBoard/microViews/Details/Details.styles";
import MenuLineItem from "components/shared/MenuLineItem/MenuLineItem";
import Session from "helpers/session";
import CheckboxGroup from "components/shared/CheckboxGroup/CheckboxGroup";
import { GlobalContext } from "context/Provider";
import { usStates, isValidEmail, monthTranslate } from "helpers/variables";
import { useTranslation } from "react-i18next";
import {
  FormInterface,
  UpsertProps,
  deviceInterface,
  orderBody
} from "./Upsert.interface";
import {
  checkImeI,
  helperTextRender,
  validatePhoneNumber
} from "./Upsert.utility";
import {COORDINATOR} from "../../../helpers/appConstants";

export default function Upsert(props: UpsertProps) {
  const [availableDevices, setAvailableDevices] = useState([]);
  const [type, setType] = useState(props.type);
  const { config } = useContext(GlobalContext);
  const [availablePrograms, setAvailablePrograms] = useState(
    config?.regOptions.programs || []
  );
  const [availableGroups, setAvailableGroups] = useState(
    config?.regOptions.groups || []
  );
  const [validEmail, setValidEmail] = useState<boolean>(false);
  const [ImeI, setImeI] = useState<string>("");
  const [isValidImeI, setIsValidImeI] = useState(true);
  const { t } = useTranslation();
  const [showError, setError] = useState<string>("");
  const [showSeverity, setSeverity] = useState<AlertColor | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [requiredDeviceArray, setRequiredDeviceArray] = useState<Array<string>>(
    [
      "Blood Glucose Monitor",
      "Blood Pressure Monitor",
      "Pulse Oxygen Monitor",
      "Weight Scale",
      "Spirometer",
      "Thermometer",
      "Cellular Hub",
      "Smart Hub"
    ]
  );

  const [form, setForm] = useState<FormInterface>({
    email: "",
    firstname: "",
    lastname: "",
    password: "",
    phone: "",
    estimatedGraduation: "",
    group: "",
    program: "",
    site: "",
    externId: "",
    gender: "other",
    dob: "",
    feet: "",
    inches: "",
    units:
      config?.basicOptions.units.toLowerCase() === "us"
        ? "Imperial"
        : config?.basicOptions.units || "",
    glucose: config?.basicOptions.glucoseUnits || "",

    pushURL: "",
    delivery: "Given to patient",
    state: "AZ",
    status: "none",
    "Blood Glucose Monitor": false,
    "Blood Pressure Monitor": false,
    "Pulse Oxygen Monitor": false,
    "Weight Scale": false,
    Spirometer: false,
    Thermometer: false,
    "Cellular Hub": false,
    "Smart Hub": false
  });

  const handleSelection = (event: any, value: any) => {
    setForm({ ...form, [event.target.id]: value });
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    setForm({ ...form, [target.name]: value });
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const validateEmail = (email: string) => {
    if (isValidEmail(email)) {
      setValidEmail(false);
      return true;
    } else {
      setValidEmail(true);
      window.scrollTo(0, 0);
      return false;
    }
  };

  // @ts-ignore
  const handleSubmission = (e) => {
    e.preventDefault();
    const {
      email,
      firstname,
      lastname,
      password,
      phone,
      estimatedGraduation,
      program,
      group,
      site,
      gender,
      dob,
      feet,
      inches,
      units,
      glucose,
      pushURL,
      status,
      externId
    } = form;
    const memberBody = {
      email: email || "",
      firstName: firstname || "",
      lastName: lastname || "",
      username: `${firstname || ""} ${lastname || ""}`,
      password: password || "simplepasswordl365",
      birthDate:
        `${dob.slice(0, 4)}${monthTranslate(dob.slice(4, -2))}${dob.slice(
          -2
        )}` || "",
      program: program || "",
      groups: group || "",
      units: {
        general: units.toLowerCase() || "imperial",
        glucose: glucose.split("/")[0] || "mg"
      },
      phone: validatePhoneNumber(phone) || "",
      site: site || "",
      externId: externId || "",
      estimatedGraduation: estimatedGraduation || "",
      gender: gender || "",
      status: status || "",
      // Add in height later wont make it this round
      height: {
        feet: feet,
        inches: inches
      }
      // Add organization field that is only visible to manager and organization call route
    };

    const coordinatorBody = {
      email: email || "",
      username: `${firstname || ""} ${lastname || ""}`,
      password: password || "simplepasswordl365",
      confidential: true,
      redirectUris: [pushURL || "http://place.holder"],
      locked: false,
      // review this access call
      access: type === "manager" ? "Provider" : "Owner",
      sources: [
        {
          name: "medm",
          autoLink: true,
          pushURL: pushURL || "http://place.holder"
        }
      ]
      // Add organization field that is only visible to manager and organization call route
    };
    const createEntity = async ()=>{
      setIsLoading(true);

      const upsertCall =
        type === "coordinator"
          ? API.kore.coordinator.upsert
          : API.kore.members.upsert;

      try {
        const upsertResponse = await upsertCall(
          "create",
          type === "coordinator" ? coordinatorBody : memberBody,
          Session.crackCookieToken("provider")
        );

        const memberId = upsertResponse.id;
        if (type === "member") {
          const coordId = Session.crackCookieUserId(COORDINATOR);
          await API.kore.members.addSingleAssociations(memberId, coordId, Session.crackCookieToken("provider"));
        }

        const {
          id: retID,
          email: retEmail,
          username: retName,
        } = type === "coordinator" ? JSON.parse(upsertResponse.text) : upsertResponse;

        if (type === "coordinator") {
          await API.kore.orders.sendWelcome(retName, retEmail, type, retID);
        } 
        // HOTFIXED DUE TO MEMBER CREATE FAILURE
        // if (type === "coordinator") {
        //   await API.kore.orders.sendWelcome(retName, retEmail, type, retID);
        // } else {
        //   await API.kore.orders.sendWelcome(
        //     upsertResponse.username,
        //     upsertResponse.email,
        //     type
        //   );
        // }

        // TODO: determine workflow before implementation of new ordering
        // if (
        //   type === "member" &&
        //   form.delivery.toLowerCase() !== "given to patient"
        // ) {
        //   try {
        //     await API.kore.orders.create(orderBody);
        //   } catch (orderErr) {
        //     console.log(orderErr);
        //   }
        // }

        window.location.pathname = `/provider/${type}s`;
      } catch (err) {
        console.log(err)
         // @ts-ignore
        if (err?.response?.status === 400) {
          setSeverity("warning");
          setError(
            "Duplicate member information, please search for this patient in the members list."
          );
        } else {
          setSeverity("error");
          setError(
            "Server failed to create user, contact support@life365.health."
          );
          setIsLoading(false);
        }
      }
    }
    if (validateEmail(email)) {
      createEntity()
    }
  };
  const getDeviceList = () => {
    API.kore.devices
      .getUnassignedInventory(
        Session.crackCookieToken("coordinator"),
        "cellular"
      )
      .then((deviceListRet) => {
        const deviceList = deviceListRet.items.map(
          (device: deviceInterface) => {
            return device.serialnumber.toUpperCase();
          }
        );
        setAvailableDevices(deviceList);
      })
      .catch((deviceErr) => console.log(deviceErr));
  };

  useEffect(() => {
    getDeviceList();
  }, []);
  return (
    <StyledContainer>
      <Grid container>
        <Grid item xs={1} />
        <Grid item xs={7}>
          <form onSubmit={handleSubmission}>
            <Stack spacing={1}>
              <MenuLineItem
                title={"Email"}
                input={
                  <TextField
                    name="email"
                    placeholder="demo@test.com"
                    onChange={handleInputChange}
                    fullWidth
                    required
                    helperText="(Required)"
                    error={validEmail}
                  />
                }
              />
              <MenuLineItem
                title={"First Name"}
                input={
                  <TextField
                    name="firstname"
                    placeholder="Bob"
                    onChange={handleInputChange}
                    fullWidth
                    required
                    helperText="(Required)"
                  />
                }
              />
              <MenuLineItem
                title={"Last Name"}
                input={
                  <TextField
                    name="lastname"
                    placeholder="Dill"
                    onChange={handleInputChange}
                    fullWidth
                    required
                    helperText="(Required)"
                  />
                }
              />
              {type === "member" && (
                <Stack>
                  <MenuLineItem
                    title={"Date of Birth"}
                    input={
                      <TextField
                        type="date"
                        name="dob"
                        placeholder="MM-DD-YYYY"
                        onChange={handleInputChange}
                        fullWidth
                        required={type !== "member" ? false : true}
                        helperText={type !== "member" ? "" : "(Required)"}
                      />
                    }
                  />
                  <MenuLineItem
                    title={"Estimated Graduation Date"}
                    input={
                      <TextField
                        type="date"
                        name="estimatedGraduation"
                        placeholder="estimatedGraduation"
                        onChange={handleInputChange}
                        fullWidth
                      />
                    }
                  />
                </Stack>
              )}
              <MenuLineItem
                title={"Phone"}
                input={
                  <TextField
                    name="phone"
                    placeholder="xxx-xxx-xxxx"
                    onChange={handleInputChange}
                    fullWidth
                    required={form.delivery === "ship to home"}
                    helperText={
                      form.delivery === "ship to home" ? "(Required)" : ""
                    }
                  />
                }
              />
              {/* <MenuLineItem title={'Group'} input={<TextField name="group" placeholder="Cardiac" onChange={handleInputChange} fullWidth/>}/>*/}
              <MenuLineItem
                title={"Group"}
                input={
                  <Autocomplete
                    id="group"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        name="group"
                        placeholder="group-1"
                        onChange={handleInputChange}
                        fullWidth
                      />
                    )}
                    options={availableGroups}
                    onChange={handleSelection}
                    fullWidth
                  />
                }
              />
              <MenuLineItem
                title={"Site"}
                input={
                  <TextField
                    name="site"
                    placeholder="main"
                    onChange={handleInputChange}
                    fullWidth
                  />
                }
              />
              {type === "member" && (
                <Stack>
                  <MenuLineItem
                    title={"Program"}
                    input={
                      <Autocomplete
                        id="program"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="program"
                            placeholder="Cardiac"
                            onChange={handleInputChange}
                            fullWidth
                          />
                        )}
                        options={availablePrograms.map((name) => {
                          return name.name;
                        })}
                        onChange={handleSelection}
                        fullWidth
                      />
                    }
                  />
                  <MenuLineItem
                    title={"MRN"}
                    input={
                      <TextField
                        name="externId"
                        placeholder="MRN"
                        onChange={handleInputChange}
                        fullWidth
                      />
                    }
                  />
                  <MenuLineItem
                    title={"Height"}
                    input={
                      <TextField
                        name="feet"
                        placeholder="Feet"
                        onChange={handleInputChange}
                      />
                    }
                  />
                  <MenuLineItem
                    title={""}
                    input={
                      <TextField
                        name="inches"
                        placeholder="Inches"
                        onChange={handleInputChange}
                      />
                    }
                  />
                  <MenuLineItem
                    title={"Gender"}
                    input={
                      <RadioGenerator
                        name="gender"
                        inputArray={["Female", "Male", "Other"]}
                        onChange={handleInputChange}
                        value={form.gender}
                      />
                    }
                  />
                  <MenuLineItem
                    title={"Admitted Status"}
                    input={
                      <RadioGenerator
                        name="status"
                        inputArray={["None", "Admitted"]}
                        onChange={handleInputChange}
                        value={form.status}
                      />
                    }
                  />
                  <MenuLineItem
                    title={"Units"}
                    input={
                      <RadioGenerator
                        name="units"
                        inputArray={["Metric", "US"]}
                        onChange={handleInputChange}
                        value={config?.basicOptions.units}
                        required
                      />
                    }
                  />
                  <MenuLineItem
                    title={"Glucose"}
                    input={
                      <RadioGenerator
                        name="glucose"
                        inputArray={["mg/dL", "mmol/L"]}
                        onChange={handleInputChange}
                        value={config?.basicOptions.glucoseUnits}
                        required
                      />
                    }
                  />
                </Stack>
              )}
              {type !== "coordinator" &&
                config?.orderOptions.newMember !== "none" && (
                  <MenuLineItem
                    title={"Delivery"}
                    input={
                      <RadioGenerator
                        name="delivery"
                        inputArray={["Ship to home", "Given to patient"]}
                        onChange={handleInputChange}
                        value={form.delivery}
                      />
                    }
                  />
                )}
              {form.delivery === "ship to home" && (
                <Stack direction="row" spacing={2}>
                  <TextField
                    name="address"
                    label="address"
                    type="text"
                    required
                    helperText="(Required)"
                  />
                  <TextField
                    name="city"
                    label="city"
                    type="text"
                    required
                    helperText="(Required)"
                  />
                  <Select
                    name="state"
                    label="state"
                    sx={{ height: "55px" }}
                    onChange={handleSelectChange}
                    value={form.state}
                    variant="outlined"
                    required
                  >
                    {usStates.map((S) => {
                      return <MenuItem value={S}>{S}</MenuItem>;
                    })}
                  </Select>
                  <TextField
                    name="zip"
                    label="zip"
                    type="number"
                    required
                    helperText="(Required)"
                  />
                </Stack>
              )}
              {type !== "coordinator" &&
                config?.orderOptions.newMember !== "none" &&
                form.delivery === "ship to home" && (
                  <MenuLineItem
                    title={"Required Devices"}
                    input={
                      <CheckboxGroup
                        itemArray={requiredDeviceArray}
                        onChange={handleInputChange}
                      />
                    }
                  />
                )}
              {(form["Cellular Hub"] || form["Smart Hub"]) &&
              form.delivery.toLowerCase() === "given to patient" ? (
                <Autocomplete
                  disablePortal
                  id="imeiValue"
                  //TODO: choose one or the other
                  // renderInput={(params)=> <TextField {...params} sx={{ width: 190}} id="imei" variant="outlined" label={'Add Device'} helperText={helperTextRender()} error={!isValidImeI} fullWidth={false} value={ImeI}/>}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{ width: 190 }}
                      id="imei"
                      variant="outlined"
                      label={t("u-add-device")}
                      helperText={helperTextRender(
                        isValidImeI,
                        t("s-p-devicesMessage-1")
                      )}
                      error={!isValidImeI}
                      onChange={(e: any) => {
                        setImeI(e.target.value);
                        setIsValidImeI(checkImeI(ImeI.length));
                      }}
                      fullWidth={false}
                      value={ImeI}
                    />
                  )}
                  options={availableDevices}
                  onChange={handleSelection}
                />
              ) : (
                ""
              )}
              <br />
            </Stack>
            <Button variant="success" type="submit" disabled={isLoading}>
              {isLoading ? "Creating..." : "Create"}
            </Button>
          </form>
          {showError !== "" ? (
            <Alert severity={showSeverity}>{showError}</Alert>
          ) : (
            ""
          )}
        </Grid>
        <Grid item xs={4} />
      </Grid>
    </StyledContainer>
  );
}
